import dayjs from "dayjs";
import { useEffect, useState } from "react";
import styled from "styled-components";

import Button from "components/Button";
import CheckBox from "components/CheckBox";
import Modal from "components/Modal";
import font from "constants/font";
import useFetchLatestNews from "hooks/api/useFetchLatestNews";
import useFetchUnreadNewsCount from "hooks/api/useFetchUnreadNewsCount";
import useUpdateNewsVisibility from "hooks/api/useUpdateNewsVisibility";
import useFirebaseContext from "hooks/useFirebaseContext";
import DateFormattedValue from "pages/News/common/DateFormattedValue";
import DurationDisplay from "pages/News/common/DurationDisplay";
import NewsNoticeLabel from "pages/News/common/NewsNoticeLabel";

const ModalContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 520px;
  max-width: 90vw;
  max-height: 80vh;
  background: white;
  border-radius: 8px;
  padding: 20px;
  overflow: hidden;
`;

const ModalBody = styled.div`
  flex-grow: 1;
  overflow-y: auto;
  max-height: 300px;
  min-height: 100px;
  background-color: #fff;
  word-wrap: break-word;
  white-space: pre-wrap;
  font-size: 14px;
  line-height: 1.6;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 12px;
`;

const DateText = styled.p`
  font-size: ${font.size14};
  color: #23221f;
  font-weight: 700; // ← 追加（必要に応じて）
  text-align: right;
  min-width: fit-content;
  margin-left: auto;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  position: sticky;
  bottom: 0;
  background-color: white;
  padding: 10px 20px;
  border-top: 1px solid #ddd;
  gap: 10px;
  padding-top: 20px;
`;
const Content = styled.div`
  font-size: ${font.size16};
  white-space: pre-wrap;
  line-height: 1.6;
  margin-left: 1px;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #ddd;
  margin: 10px 0;
`;

const LabelWrapper = styled.div`
  width: 130px;
`;
const DurationWrap = styled.div`
  margin: 24px 0;
  font-weight: bold;
`;
const DurationTitle = styled.div`
  font-size: ${font.size14};
  padding-bottom: 2px;
`;

const NewsModal = () => {
  const { latestNews, isLoading, mutate } = useFetchLatestNews();
  const { auth } = useFirebaseContext();
  const { updateVisibility } = useUpdateNewsVisibility();
  const { mutate: mutateUnreadCount } = useFetchUnreadNewsCount();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [doNotDisplayAgain, setDoNotDisplayAgain] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (latestNews && !isLoading && !latestNews.doNotDisplayAgain) {
      setIsModalOpen(true);
      setDoNotDisplayAgain(false);
    }
  }, [latestNews, isLoading]);

  const handleCheckboxChange = () => {
    setDoNotDisplayAgain((prev) => !prev);
  };

  const handleCloseModal = async () => {
    setIsModalOpen(false);

    if (latestNews && doNotDisplayAgain && auth?.currentUser) {
      setIsUpdating(true);
      try {
        await updateVisibility(latestNews.id, true);
        await mutate();
        await mutateUnreadCount();
      } catch (error) {
        console.error("API 更新失敗:", error);
      } finally {
        setIsUpdating(false);
      }
    }
  };

  if (!latestNews) return null;

  return (
    <Modal isVisible={isModalOpen} onClose={handleCloseModal} width="600px" height="auto">
      <ModalContentWrapper>
        <ModalHeader>
          <LabelWrapper>
            <NewsNoticeLabel type={latestNews.category} />
          </LabelWrapper>
          <DateText>
            <DateFormattedValue createdAt={latestNews.createdAt} isBold />
          </DateText>
        </ModalHeader>
        <Divider />
        <ModalBody>
          <div style={{ fontSize: font.size20, fontWeight: "bold", marginBottom: "10px" }}>{latestNews.title}</div>
          <Content>
            {latestNews.content}
            {latestNews.category === "メンテナンス情報" && latestNews.startAt && latestNews.endAt && (
              <DurationWrap>
                <DurationTitle>メンテナンス期間</DurationTitle>
                <DurationDisplay endAt={latestNews.endAt} startAt={latestNews.startAt} />
              </DurationWrap>
            )}
          </Content>
        </ModalBody>
        <Footer>
          <CheckBox
            id="doNotDisplayAgain"
            checked={doNotDisplayAgain}
            onCheck={handleCheckboxChange}
            label="今後表示しない"
            fontSize={font.size14}
          />
          <Button
            label="閉じる"
            onClick={handleCloseModal}
            type="secondary"
            width="160px"
            height="48px"
            paddingHorizontal="10px"
            disabled={isUpdating}
          />
        </Footer>
      </ModalContentWrapper>
    </Modal>
  );
};

export default NewsModal;
